import { useState, useRef, useEffect, useMemo } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, Plus, FileText, Download, X, Search, Trash2, Package, ChevronDown, Clock, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { modalV2OverlayClass } from '../utils/modalUiV2';
import { useV3Window } from '../v3/useV3Window';

const API = process.env.REACT_APP_BACKEND_URL;
const ORANGE = '#ee7203';
const ALL_TAB_ID = '__all__';

// Helper function to format currency
const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Shared UOM options for "Qty UOM" (stock unit) and "Price UOM" (the unit the
// price is quoted per) dropdowns — mirrors Quotes.jsx's Cost Factor engine.
const UOM_OPTIONS = ['Each', 'M2', 'Length', 'M'];

// ── Recent Pro Formas View ─────────────────────────────────────────────────────
const ProformaCard = ({ item, onSelect }) => {
  const date = item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const pdfUrl = item.invoice_pdf_id ? `${API}/api/proforma/pdf/${item.invoice_pdf_id}` : null;

  return (
    <div
      data-testid={`proforma-card-${item.id}`}
      onClick={() => onSelect?.(item)}
      className="group rounded-xl border border-gray-100 bg-white p-3.5 shadow-sm hover:shadow-md transition-all hover:border-orange-300 cursor-pointer flex flex-col gap-2"
      title="Click to open and edit this pro forma"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-sm text-gray-900 leading-tight line-clamp-2 flex-1 group-hover:text-orange-600 transition-colors">{item.customer_name || 'Unknown Customer'}</p>
        {item.is_draft ? (
          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700" data-testid={`proforma-card-draft-${item.id}`}>DRAFT</span>
        ) : item.paid_status ? (
          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">PAID</span>
        ) : (
          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">UNPAID</span>
        )}
      </div>
      <div className="text-xs text-gray-500 font-mono">{item.quote_reference || '—'}</div>
      <div className="flex items-center justify-between mt-auto pt-1 border-t border-gray-50">
        <span className="text-xs font-bold" style={{ color: ORANGE }}>£{formatCurrency(item.grand_total)}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">{date}</span>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              data-testid={`proforma-card-pdf-${item.id}`}
              className="text-[10px] font-semibold px-2 py-0.5 rounded-lg transition-colors text-white"
              style={{ background: ORANGE }}
            >
              PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const RecentProformasView = ({ user, onSelect }) => {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [activeTab, setActiveTab] = useState(ALL_TAB_ID);
  const [searchQuery, setSearchQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/api/proforma/recent/all`).catch(() => ({ data: { proformas: [], current_user_id: null } }));
      setAllItems(res.data.proformas || []);
      const uid = res.data.current_user_id || user?.id || null;
      setCurrentUserId(uid);
      setActiveTab(uid || ALL_TAB_ID);
    } catch {
      toast.error('Failed to load recent pro formas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const userTabs = useMemo(() => {
    const map = {};
    allItems.forEach(p => {
      if (!map[p.user_id]) map[p.user_id] = { user_id: p.user_id, label: p.user_label || p.user_id };
    });
    if (currentUserId && !map[currentUserId]) {
      const label = user?.full_name || user?.username || user?.email || currentUserId;
      map[currentUserId] = { user_id: currentUserId, label };
    }
    return Object.values(map).sort((a, b) => {
      if (a.user_id === currentUserId) return -1;
      if (b.user_id === currentUserId) return 1;
      return (a.label || '').localeCompare(b.label || '');
    });
  }, [allItems, currentUserId, user]);

  const baseItems = useMemo(() => {
    const pool = activeTab === ALL_TAB_ID ? allItems : allItems.filter(p => p.user_id === activeTab);
    return [...pool].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [allItems, activeTab]);

  const visibleItems = useMemo(() => {
    if (!searchQuery.trim()) return baseItems;
    const q = searchQuery.toLowerCase();
    return baseItems.filter(p =>
      (p.customer_name || '').toLowerCase().includes(q) ||
      (p.quote_reference || '').toLowerCase().includes(q)
    );
  }, [baseItems, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading pro formas…</span>
      </div>
    );
  }

  if (allItems.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-200" />
        <p className="font-medium">No pro formas in the past 30 days</p>
        <p className="text-sm mt-1">Create a pro forma to get started</p>
      </div>
    );
  }

  return (
    <div>
      {/* ── User tabs ───────────────────────────────────────────────────────── */}
      {userTabs.length >= 1 && (
        <div className="flex items-end gap-1 border-b border-gray-100 mb-4 overflow-x-auto pb-0" style={{ scrollbarWidth: 'none' }}>
          <button
            key={ALL_TAB_ID}
            onClick={() => setActiveTab(ALL_TAB_ID)}
            data-testid="pf-tab-all"
            className={`flex-shrink-0 px-3.5 py-2 rounded-t-lg text-xs font-semibold border-t border-l border-r -mb-px transition-colors whitespace-nowrap ${
              activeTab === ALL_TAB_ID ? 'text-white border-orange-400' : 'text-gray-500 bg-gray-50 border-transparent hover:text-gray-900'
            }`}
            style={activeTab === ALL_TAB_ID ? { background: ORANGE, borderColor: ORANGE } : {}}
          >
            All <span className={`ml-1 ${activeTab === ALL_TAB_ID ? 'opacity-80' : 'text-gray-400'}`}>({allItems.length})</span>
          </button>
          {userTabs.map(tab => {
            const isActive = activeTab === tab.user_id;
            const isOwn = tab.user_id === currentUserId;
            const count = allItems.filter(p => p.user_id === tab.user_id).length;
            return (
              <button
                key={tab.user_id}
                onClick={() => setActiveTab(tab.user_id)}
                data-testid={`pf-tab-${tab.user_id}`}
                className={`flex-shrink-0 px-3.5 py-2 rounded-t-lg text-xs font-semibold border-t border-l border-r -mb-px transition-colors whitespace-nowrap ${
                  isActive ? 'text-white border-orange-400' : 'text-gray-500 bg-gray-50 border-transparent hover:text-gray-900'
                }`}
                style={isActive ? { background: ORANGE, borderColor: ORANGE } : {}}
              >
                {tab.label}{isOwn ? ' (me)' : ''} <span className={`ml-1 ${isActive ? 'opacity-80' : 'text-gray-400'}`}>({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Search bar ──────────────────────────────────────────────────────── */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search customer name or pro forma ref…"
          data-testid="recent-proformas-search"
          className="w-full pl-8 pr-8 py-2 rounded-lg border border-gray-200 text-xs text-gray-700 placeholder-gray-300 focus:outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-200 bg-gray-50"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* ── Count + Refresh ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-500">
          {visibleItems.length}{searchQuery ? ` of ${baseItems.length}` : ''} pro forma{visibleItems.length !== 1 ? 's' : ''} — past 30 days
        </span>
        <button onClick={load} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {visibleItems.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Clock className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-sm">{searchQuery ? `No results for "${searchQuery}"` : 'No pro formas for this user in the past month'}</p>
          {searchQuery && <button onClick={() => setSearchQuery('')} className="mt-2 text-xs text-orange-500 hover:underline">Clear search</button>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto pr-1">
          {visibleItems.map(item => <ProformaCard key={item.id} item={item} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
};


const ProductSearchDropdown = ({ value, onChange, onSelect, onBlur, placeholder }) => {
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInStockOnly, setShowInStockOnly] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setSearchQuery(value || '');
  }, [value]);

  // Calculate dropdown position when opening - align to the row's Code column
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      // Find the parent row (grid container)
      const row = inputRef.current.closest('.grid');
      if (row) {
        const rowRect = row.getBoundingClientRect();
        // Find the modal content to constrain width
        const modal = inputRef.current.closest('[role="dialog"]') || inputRef.current.closest('.rounded-2xl');
        const modalRect = modal ? modal.getBoundingClientRect() : null;
        
        // Calculate width to fit within modal (with padding)
        const maxWidth = modalRect ? modalRect.right - rowRect.left - 20 : 900;
        
        setDropdownPosition({
          top: inputRect.bottom + 4,
          left: rowRect.left,
          width: Math.min(maxWidth, 900)
        });
      } else {
        setDropdownPosition({
          top: inputRect.bottom + 4,
          left: inputRect.left,
          width: 900
        });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Control' && isOpen) {
        setShowInStockOnly(prev => !prev);
        toast.info(showInStockOnly ? 'Showing all products' : 'Showing in-stock only', { duration: 1500 });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, showInStockOnly]);

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`${API}/api/stock-database/search`, {
        params: { q: query, limit: 100, include_all: true }
      });
      setResults(response.data?.results || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    onChange(newValue);
    setIsOpen(true);
    searchProducts(newValue);
  };

  const handleSelectProduct = (product) => {
    onSelect(product);
    setIsOpen(false);
    setResults([]);
  };

  const hasStock = (item) => {
    const inStock = item.on_hand_quantity || item.on_hand_balance || item.free_stock || 0;
    const onOrder = item.on_order_quantity || 0;
    return inStock > 0 || onOrder > 0;
  };

  const filteredResults = showInStockOnly ? results.filter(hasStock) : results;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative" ref={inputRef}>
        <Input
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => searchQuery.length >= 2 && setIsOpen(true)}
          onBlur={() => { if (onBlur) setTimeout(() => onBlur(searchQuery), 150); }}
          placeholder={placeholder}
          className="pr-8 w-full"
        />
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      </div>
      
      {isOpen && (searchQuery.length >= 2) && (
        <div 
          className="fixed z-[9999] rounded-lg shadow-2xl overflow-hidden"
          style={{
            background: 'rgba(35, 35, 35, 0.98)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(238, 114, 3, 0.6)',
            width: `${dropdownPosition.width}px`,
            maxHeight: '300px',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <div className="sticky top-0 px-4 py-3 text-xs flex items-center justify-between border-b border-white/20" style={{ background: 'rgba(35, 35, 35, 0.98)' }}>
            <span className="text-gray-300 font-medium">
              {showInStockOnly ? '📦 In-Stock Only' : '📋 All Products'} ({filteredResults.length} results)
            </span>
            <span className="text-gray-500">Press Ctrl to toggle stock filter</span>
          </div>
          
          <div className="overflow-y-auto" style={{ maxHeight: '350px' }}>
            {loading ? (
              <div className="p-6 text-center text-gray-400">Searching...</div>
            ) : filteredResults.length > 0 ? (
              <div className="divide-y divide-white/10">
                {filteredResults.map((product, idx) => {
                  const inStock = product.on_hand_quantity || product.on_hand_balance || product.free_stock || 0;
                  const onOrder = product.on_order_quantity || 0;
                  const freeStock = product.free_stock || 0;
                  const isOutOfStock = inStock <= 0 && onOrder <= 0;
                  
                  return (
                    <div
                      key={idx}
                      className={`p-3 cursor-pointer hover:bg-white/10 transition-colors ${isOutOfStock ? 'opacity-50' : ''}`}
                      onClick={() => handleSelectProduct(product)}
                    >
                      <div className="flex items-start gap-4">
                        <Package className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#ee7203' }} />
                        <div className="flex-1 min-w-0 grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-2">
                            <div className="font-bold text-sm text-white">
                              {product.product_code || product.code}
                            </div>
                          </div>
                          <div className="col-span-6">
                            <div className="text-sm text-gray-200">
                              {product.description || product.name}
                            </div>
                          </div>
                          <div className="col-span-4 flex gap-4 text-xs">
                            <span className={inStock > 0 ? 'text-green-400' : 'text-gray-500'}>
                              <span className="text-gray-400">Stock:</span> {inStock}
                            </span>
                            <span className={freeStock > 0 ? 'text-blue-400' : 'text-gray-500'}>
                              <span className="text-gray-400">Free:</span> {freeStock}
                            </span>
                            <span className={onOrder > 0 ? 'text-yellow-400' : 'text-gray-500'}>
                              <span className="text-gray-400">Order:</span> {onOrder}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-400">No products found for "{searchQuery}"</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Pro Forma Modal Component
const ProformaModal = ({ isOpen, onClose, mode, user, resumeSnapshot = null }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const activityIdRef = useRef(resumeSnapshot?.activityId || null);
  const fileInputRef = useRef(null);
  const v3w = useV3Window(isOpen, 1200, { single: true });
  const [selectedFile, setSelectedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentMode, setCurrentMode] = useState(resumeSnapshot?.currentMode || mode);
  const [prevMode, setPrevMode] = useState(resumeSnapshot?.prevMode || null); // Track mode we came from (e.g. 'recent' → 'create')
  const [loadingForEdit, setLoadingForEdit] = useState(false);
  
  // Reset mode when modal opens (skipped when resuming from the Activity Bar
  // — the restored state below should be kept, not overwritten)
  useEffect(() => {
    if (resumeSnapshot) return;
    setCurrentMode(mode);
    setPrevMode(null);
    if (mode === 'create') {
      setProformaForm({
        customer_name: '',
        customer_address: '',
        proforma_number: `PF-${Date.now()}`,
        document_type: 'proforma', // Default to Pro Forma Invoice
        date: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 21*24*60*60*1000).toISOString().split('T')[0],
        items: [{ code: '', description: '', quantity: 1, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null }],
        notes: '',
        _editingProformaId: null,
        terms: `• All goods and services are supplied in accordance with our standard terms and conditions - these are available from https://www.encon.co.uk/terms-conditions
• We reserve the right to vary prices for individual items if this quotation is not accepted in full.
• This pro forma invoice is valid for 21 days from date of issue unless otherwise stated and agreed.
• Payment is required in advance before dispatch of goods.
• Whilst every attempt has been made to ensure that the prices and information given are accurate, Encon take no responsibility for any errors and omissions.
• All prices are subject to VAT, charged at the appropriate rate.`
      });
    }
  }, [mode, isOpen]);
  
  const [proformaForm, setProformaForm] = useState(resumeSnapshot?.proformaForm || {
    customer_name: '',
    customer_address: '',
    proforma_number: `PF-${Date.now()}`,
    document_type: 'proforma', // 'proforma' or 'invoice'
    date: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 21*24*60*60*1000).toISOString().split('T')[0],
    items: [{ code: '', description: '', quantity: 1, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null }],
    notes: '',
    terms: `• All goods and services are supplied in accordance with our standard terms and conditions - these are available from https://www.encon.co.uk/terms-conditions
• We reserve the right to vary prices for individual items if this quotation is not accepted in full.
• This pro forma invoice is valid for 21 days from date of issue unless otherwise stated and agreed.
• Payment is required in advance before dispatch of goods.
• Whilst every attempt has been made to ensure that the prices and information given are accurate, Encon take no responsibility for any errors and omissions.
• All prices are subject to VAT, charged at the appropriate rate.`
  });

  // Modal UI v2 — register as a resumable "In Progress" activity once the
  // user is actually building/editing a pro forma (mirrors StockRequestModal).
  useEffect(() => {
    if (!isOpen || !modalUiV2 || currentMode !== 'create' || activityIdRef.current) return;
    activityIdRef.current = resumeSnapshot?.activityId || `proforma-${Date.now()}`;
    activityBar.openActivity({
      type: 'proforma',
      activityId: activityIdRef.current,
      title: `(PF) - ${proformaForm.customer_name || 'New Pro Forma'}`,
      subtitle: 'Pro Forma',
      snapshot: { activityId: activityIdRef.current, currentMode, prevMode, proformaForm },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, currentMode]);

  // Keep the live snapshot fresh as the form is edited.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !activityIdRef.current) return;
    activityBar.updateActiveSnapshot(
      { activityId: activityIdRef.current, currentMode, prevMode, proformaForm },
      { title: `(PF) - ${proformaForm.customer_name || 'New Pro Forma'}`, subtitle: 'Pro Forma' }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, proformaForm, currentMode, prevMode]);

  // Ends this pro forma's Activity Bar tracking — either minimized into "In
  // Progress" (still unfinished, resumable later) or fully discarded (just
  // completed — see the PDF-generated success handler below, which logs it
  // into "Recents" first). The Recent Pro Formas BROWSE list itself never
  // gets its own tab entry — only actual pro forma drafts do.
  const endProformaActivity = (minimize) => {
    if (!modalUiV2 || !activityIdRef.current) return;
    if (minimize) activityBar.minimizeActive(); else activityBar.discardActive();
    activityIdRef.current = null;
  };

  // Modal UI v2 keeps the header interactive above the overlay — lock page scroll.
  useEffect(() => {
    if (!isOpen || !modalUiV2) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen, modalUiV2]);

  // Auto-save timer — persists an in-progress pro forma to the DB every 30s
  // while in 'create' mode (mirrors Quotes' autosave), so it shows up in
  // Recent Pro Formas even if the modal is closed/navigated away before a
  // PDF is ever generated. Upserts by `_editingProformaId`.
  useEffect(() => {
    if (!isOpen || currentMode !== 'create') return;
    const autoSaveTimer = setInterval(async () => {
      if (proformaForm.customer_name || proformaForm.items.some(i => i.description)) {
        try {
          const res = await api.post(`${API}/api/proforma/autosave`, {
            ...proformaForm,
            proforma_id: proformaForm._editingProformaId,
          });
          if (!proformaForm._editingProformaId && res.data.proforma_id) {
            setProformaForm(prev => ({ ...prev, _editingProformaId: res.data.proforma_id }));
          }
        } catch (error) {
          console.error('Pro forma auto-save failed:', error);
        }
      }
    }, 30000);
    return () => clearInterval(autoSaveTimer);
  }, [isOpen, currentMode, proformaForm]);

  const addItem = () => {
    setProformaForm(prev => ({
      ...prev,
      items: [...prev.items, { code: '', description: '', quantity: 1, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null }]
    }));
  };

  const removeItem = (index) => {
    if (proformaForm.items.length > 1) {
      setProformaForm(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateItem = (index, field, value) => {
    setProformaForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const handleProductSelect = (index, product) => {
    setProformaForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? {
        ...item,
        code: product.product_code || product.code || '',
        description: product.description || product.name || '',
        cost_factor: product.cost_factor != null ? product.cost_factor : item.cost_factor
      } : item)
    }));
    applyPricingLookup(index, product.product_code || product.code || '');
  };

  // Looks up Settings > Pricing (List Price + Price UOM) for a selected/typed
  // product code and defaults the line's Unit Price + Price UOM — mirrors
  // Create Quote's Calculator auto-fill (Proforma has no calculator panel, so
  // this sets Unit Price directly). Non-fatal if the code isn't priced yet.
  const applyPricingLookup = async (index, code) => {
    if (!code || !code.trim()) return;
    try {
      const res = await api.get(`${API}/api/pricing/lookup/${encodeURIComponent(code.trim())}`);
      if (!res.data?.found) return;
      const { list_price, quote_uom } = res.data;
      setProformaForm(prev => ({
        ...prev,
        items: prev.items.map((item, i) => i === index ? { ...item, unit: quote_uom || item.unit, unit_price: list_price } : item)
      }));
    } catch { /* non-fatal — product simply isn't priced in Settings > Pricing yet */ }
  };

  // Look up the M2/coverage cost factor for a manually-typed product code
  // (dropdown selections already carry cost_factor via handleProductSelect).
  const lookupCostFactorForCode = async (index, code) => {
    if (!code || !code.trim()) return;
    try {
      const res = await api.get(`${API}/api/stock-database/cost-factor/${encodeURIComponent(code.trim())}`);
      if (res.data?.found) {
        updateItem(index, 'cost_factor', res.data.cost_factor);
      }
    } catch { /* non-fatal — leave cost factor as-is */ }
    applyPricingLookup(index, code);
  };

  // Cost Factor engine — identical to Create Quote. Converts M2/coverage
  // quantities & prices into stock-unit totals.
  const getItemCostFactor = (item) => {
    const cf = parseFloat(item.cost_factor);
    return (!isNaN(cf) && cf > 0) ? cf : 1;
  };

  const getQtyEach = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const qtyUnit = item.qty_unit || 'Each';
    if (qtyUnit !== 'Each') {
      return Math.ceil(qty / getItemCostFactor(item));
    }
    return qty;
  };

  const getLineTotal = (item) => {
    const qtyEach = getQtyEach(item);
    const price = parseFloat(item.unit_price) || 0;
    const priceUnit = item.unit || 'Each';
    const cf = priceUnit !== 'Each' ? getItemCostFactor(item) : 1;
    return Math.round(price * cf * qtyEach * 100) / 100;
  };

  const calculateSubtotal = () => proformaForm.items.reduce((sum, item) => sum + getLineTotal(item), 0);
  const calculateVAT = () => calculateSubtotal() * 0.2;
  const calculateTotal = () => calculateSubtotal() + calculateVAT();
  // Show the Cost Factor / Qty UOM / Qty(Each) columns only once a line
  // actually uses something other than plain "Each".
  const hasM2Data = proformaForm.items.some(it => (it?.qty_unit || 'Each') !== 'Each' || (it?.unit || 'Each') !== 'Each');
  // The Qty(Each) column is only meaningful when a line's Qty UOM actually needs
  // converting from M2/Length coverage into stock units — hide it otherwise.
  const hasQtyConversion = proformaForm.items.some(it => (it?.qty_unit || 'Each') !== 'Each');
  // NOTE: Tailwind's JIT scanner requires complete literal class strings — it
  // cannot resolve `col-span-[${variable}]` from a runtime template literal.
  const m2DescGridColumn = hasQtyConversion ? 'span 11 / span 11' : 'span 13 / span 13';

  // Fetch full details of a proforma from "Recent Pro Formas" and load it into
  // the editor so it can be amended and re-downloaded (updates the same record).
  const handleSelectForEdit = async (item) => {
    setLoadingForEdit(true);
    try {
      const res = await api.get(`${API}/api/proforma/${item.id}`);
      const full = res.data;
      setProformaForm(prev => ({
        ...prev,
        customer_name: full.customer_name || '',
        customer_address: full.customer_address || '',
        proforma_number: full.quote_reference || '',
        document_type: full.document_type || 'proforma',
        date: full.quote_date || prev.date,
        items: (full.line_items || []).length > 0
          ? full.line_items.map(li => ({
              code: li.product_code || '',
              description: li.description || '',
              quantity: li.quantity || 1,
              qty_unit: li.qty_unit || 'Each',
              unit: li.unit || 'Each',
              unit_price: li.unit_price || 0,
              cost_factor: li.cost_factor != null ? li.cost_factor : null,
            }))
          : [{ code: '', description: '', quantity: 1, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null }],
        notes: full.notes || '',
        _editingProformaId: full.id,
      }));
      setPrevMode('recent');
      setCurrentMode('create');
      toast.success('Pro forma loaded — continue editing');
    } catch (error) {
      console.error('Error loading pro forma for edit:', error);
      toast.error('Failed to load pro forma');
    } finally {
      setLoadingForEdit(false);
    }
  };

  const generateAndDownloadPDF = async () => {
    if (!proformaForm.customer_name.trim()) {
      toast.error('Please enter a customer name');
      return;
    }
    if (proformaForm.items.length === 0 || !proformaForm.items.some(i => i.description)) {
      toast.error('Please add at least one item with a description');
      return;
    }

    const isEditing = !!proformaForm._editingProformaId;

    try {
      setProcessing(true);
      
      const response = await api.post(`${API}/api/proforma/create-download`, {
        ...proformaForm,
        proforma_id: proformaForm._editingProformaId || undefined,
        subtotal: calculateSubtotal(),
        vat: calculateVAT(),
        total: calculateTotal()
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filePrefix = proformaForm.document_type === 'invoice' ? 'Invoice' : 'ProForma';
      link.setAttribute('download', `${filePrefix}_${proformaForm.proforma_number}_${proformaForm.customer_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const docLabel = proformaForm.document_type === 'invoice' ? 'Invoice' : 'Pro Forma';
      toast.success(isEditing ? `${docLabel} updated and downloaded successfully!` : `${docLabel} PDF downloaded successfully!`);
      // Completed (PDF generated) — moves from "In Progress" into "Recents"
      // as this specific pro forma, not the Recent Pro Formas browse page.
      if (modalUiV2 && activityIdRef.current) {
        activityBar.logRecentView({
          type: 'proforma',
          activityId: activityIdRef.current,
          title: `(PF) - ${proformaForm.customer_name || 'Pro Forma'}`,
          subtitle: 'Completed',
          snapshot: { activityId: activityIdRef.current, currentMode: 'create', prevMode, proformaForm },
        });
      }
      endProformaActivity(false);
      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    try {
      setProcessing(true);
      const formData = new FormData();
      formData.append('file', file);

      // Call extract endpoint to get JSON data
      const response = await api.post(`${API}/api/proformas/extract-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success && response.data.data) {
        const extracted = response.data.data;
        
        // Populate form with extracted data
        setProformaForm(prev => ({
          ...prev,
          customer_name: extracted.customer_name || prev.customer_name,
          customer_address: extracted.customer_address || prev.customer_address,
          proforma_number: extracted.proforma_number || `PF-${Date.now()}`,
          date: extracted.date || prev.date,
          valid_until: extracted.valid_until || prev.valid_until,
          items: extracted.items && extracted.items.length > 0 
            ? extracted.items.map(it => ({
                code: it.code || '',
                description: it.description || '',
                quantity: it.quantity || 1,
                qty_unit: it.qty_unit || 'Each',
                unit: it.unit || 'Each',
                unit_price: it.unit_price || 0,
                cost_factor: it.cost_factor != null ? it.cost_factor : null,
              }))
            : [{ code: '', description: '', quantity: 1, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null }],
          notes: extracted.notes || prev.notes,
        }));
        
        // Switch to create mode to show the form
        setCurrentMode('create');
        setSelectedFile(null);
        toast.success('PDF data extracted! Review and edit the details below.');

        // Backfill cost factors for any parsed item that didn't already get one
        // from the PDF's own Stock/Pricing unit price columns.
        (extracted.items || []).forEach((it, idx) => {
          if (it.code && (it.cost_factor == null)) {
            lookupCostFactorForCode(idx, it.code);
          }
        });
      } else {
        toast.error(response.data.error || 'Failed to extract data from PDF');
      }
    } catch (error) {
      console.error('Error processing upload:', error);
      toast.error('Failed to process uploaded file');
    } finally {
      setProcessing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  if (!isOpen) return null;

  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    border: '3px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  };

  return (
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center p-4 ${modalV2OverlayClass(modalUiV2)} ${v3w.className}`}
      {...v3w.attrs}
      style={{ ...(modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 50 }), ...v3w.style }}
    >
      <div
        className="w-full max-w-6xl overflow-y-auto relative"
        style={{ ...modalStyle, maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '90vh' }}
      >
        {/* Red X Close Button — unfinished ('create' mode) work is autosaved
            (so it shows up in Recent Pro Formas) and minimized into the
            "In Progress" Activity Bar tab instead of discarded. */}
        <button 
          onClick={async () => {
            if (currentMode === 'create' && (proformaForm.customer_name || proformaForm.items?.some(i => i.description))) {
              try {
                await api.post(`${API}/api/proforma/autosave`, {
                  ...proformaForm,
                  proforma_id: proformaForm._editingProformaId,
                });
              } catch { /* non-fatal */ }
            }
            endProformaActivity(currentMode === 'create');
            onClose();
          }}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {currentMode === 'recent' ? (
          <>
            {/* Recent Pro Formas Mode */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <Clock className="w-7 h-7" style={{ color: '#ee7203' }} />
                <h2 className="text-xl font-bold" style={{ color: '#ee7203' }}>Recent Pro Formas</h2>
                <span className="text-xs text-gray-400 ml-auto">Past 30 days · Team view · Click a card to edit</span>
              </div>
              {loadingForEdit ? (
                <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading pro forma…</span>
                </div>
              ) : (
                <RecentProformasView user={user} onSelect={handleSelectForEdit} />
              )}
            </div>
          </>
        ) : currentMode === 'create' ? (
          <>
            <div className="v3-modal-bar sticky top-0 bg-white rounded-t-xl p-6 border-b z-[5]">
              <div className="flex items-center gap-3">
                {prevMode === 'recent' && (
                  <button
                    onClick={() => { endProformaActivity(true); setPrevMode(null); setCurrentMode('recent'); }}
                    className="text-xs text-gray-500 hover:text-orange-600 flex items-center gap-1 transition-colors mr-1"
                    data-testid="back-to-recent-proformas-btn"
                  >
                    ← Back
                  </button>
                )}
                <FileText className="w-8 h-8" style={{ color: '#ee7203' }} />
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#ee7203' }}>
                    {proformaForm._editingProformaId ? 'Edit Pro Forma' : 'Create Pro Forma'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {proformaForm._editingProformaId ? 'Changes update the existing record' : 'Download as PDF'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Document Type Selection */}
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <Label className="text-sm font-semibold whitespace-nowrap">Document Type:</Label>
                <Select 
                  value={proformaForm.document_type} 
                  onValueChange={(val) => setProformaForm(prev => ({ ...prev, document_type: val }))}
                >
                  <SelectTrigger className="w-48 bg-white">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="proforma">Pro Forma Invoice</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold">Customer Name *</Label>
                    <Input
                      value={proformaForm.customer_name}
                      onChange={(e) => setProformaForm(prev => ({ ...prev, customer_name: e.target.value }))}
                      placeholder="Enter customer name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Customer Address</Label>
                    <Textarea
                      value={proformaForm.customer_address}
                      onChange={(e) => setProformaForm(prev => ({ ...prev, customer_address: e.target.value }))}
                      placeholder="Enter customer address"
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold">Pro Forma Ref</Label>
                      <Input
                        value={proformaForm.proforma_number}
                        onChange={(e) => setProformaForm(prev => ({ ...prev, proforma_number: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Date</Label>
                      <Input
                        type="date"
                        value={proformaForm.date}
                        onChange={(e) => setProformaForm(prev => ({ ...prev, date: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Valid Until</Label>
                    <Input
                      type="date"
                      value={proformaForm.valid_until}
                      onChange={(e) => setProformaForm(prev => ({ ...prev, valid_until: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <Label className="text-sm font-semibold">Line Items</Label>
                  <Button size="sm" onClick={addItem} style={{ backgroundColor: '#ee7203' }}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                <div className="rounded-xl border-2 border-gray-200 overflow-hidden">
                  <div className={`grid ${hasM2Data ? 'grid-cols-[repeat(36,minmax(0,1fr))]' : 'grid-cols-12'} gap-2 p-3 text-xs font-semibold text-white`} style={{ backgroundColor: '#ee7203' }}>
                    <div className={hasM2Data ? 'col-span-6' : 'col-span-2'}>CODE</div>
                    <div className={!hasM2Data ? 'col-span-5' : undefined} style={hasM2Data ? { gridColumn: m2DescGridColumn } : undefined}>DESCRIPTION</div>
                    {hasM2Data && <div className="col-span-2 text-center" title="M2/Length coverage per stock unit">CF</div>}
                    <div className={hasM2Data ? 'col-span-2 text-center' : 'col-span-1 text-center'}>QTY</div>
                    {hasM2Data && <div className="col-span-3 text-center">QTY UOM</div>}
                    {hasM2Data && hasQtyConversion && <div className="col-span-2 text-center">QTY (EA)</div>}
                    {hasM2Data ? <div className="col-span-3 text-center">PRICE UOM</div> : <div className="col-span-1 text-center">UNIT</div>}
                    <div className={hasM2Data ? 'col-span-3 text-center' : 'col-span-1 text-center'}>
                      {!hasM2Data ? 'UNIT PRICE' : proformaForm.items.some(it => (it.unit || 'Each') === 'M2') ? 'M2 PRICE' : proformaForm.items.some(it => (it.unit || 'Each') === 'M') ? 'M PRICE' : 'UNIT PRICE'}
                    </div>
                    <div className={hasM2Data ? 'col-span-2 text-center' : 'col-span-1 text-center'}>TOTAL</div>
                    <div className={hasM2Data ? 'col-span-2' : 'col-span-1'}></div>
                  </div>
                  
                  <div className="divide-y max-h-[300px] overflow-y-auto">
                    {proformaForm.items.map((item, index) => (
                      <div key={index} className={`grid ${hasM2Data ? 'grid-cols-[repeat(36,minmax(0,1fr))]' : 'grid-cols-12'} gap-2 p-3 items-center bg-white hover:bg-gray-50`}>
                        <div className={hasM2Data ? 'col-span-6' : 'col-span-2'}>
                          <ProductSearchDropdown
                            value={item.code}
                            onChange={(val) => updateItem(index, 'code', val)}
                            onSelect={(product) => handleProductSelect(index, product)}
                            onBlur={(val) => lookupCostFactorForCode(index, val)}
                            placeholder="Code..."
                            data-testid={`pf-item-code-${index}`}
                          />
                        </div>
                        <div className={!hasM2Data ? 'col-span-5' : undefined} style={hasM2Data ? { gridColumn: m2DescGridColumn } : undefined}>
                          <ProductSearchDropdown
                            value={item.description}
                            onChange={(val) => updateItem(index, 'description', val)}
                            onSelect={(product) => handleProductSelect(index, product)}
                            placeholder="Description..."
                          />
                        </div>
                        {hasM2Data && (
                          <div className="col-span-2">
                            <Input
                              type="number"
                              step="0.001"
                              min="0"
                              value={item.cost_factor ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                updateItem(index, 'cost_factor', raw === '' ? null : (parseFloat(raw) || 0));
                              }}
                              className="text-center text-sm"
                              placeholder="1"
                              title="Cost Factor — M2 covered per stock unit"
                              data-testid={`pf-item-cost-factor-${index}`}
                            />
                          </div>
                        )}
                        <div className={hasM2Data ? 'col-span-2' : 'col-span-1'}>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="text-center text-sm"
                            data-testid={`pf-item-qty-${index}`}
                          />
                        </div>
                        {hasM2Data && (
                          <div className="col-span-3">
                            <Select value={item.qty_unit || 'Each'} onValueChange={(val) => updateItem(index, 'qty_unit', val)}>
                              <SelectTrigger className="text-xs" data-testid={`pf-item-qty-uom-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                {UOM_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {hasM2Data && hasQtyConversion && (
                          <div className="col-span-2 text-center text-sm font-semibold text-gray-700" data-testid={`pf-item-qty-each-${index}`}>
                            {getQtyEach(item)}
                          </div>
                        )}
                        <div className={hasM2Data ? 'col-span-3' : 'col-span-1'}>
                          <Select value={item.unit} onValueChange={(val) => updateItem(index, 'unit', val)}>
                            <SelectTrigger className="text-sm" data-testid={`pf-item-price-uom-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              {UOM_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className={hasM2Data ? 'col-span-3' : 'col-span-1'}>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="text-center text-sm"
                            placeholder="£0.00"
                            data-testid={`pf-item-unit-price-${index}`}
                          />
                        </div>
                        <div className={`${hasM2Data ? 'col-span-2' : 'col-span-1'} text-center font-semibold text-sm`} data-testid={`pf-item-line-total-${index}`}>
                          £{formatCurrency(getLineTotal(item))}
                        </div>
                        <div className={hasM2Data ? 'col-span-2 text-center' : 'col-span-1 text-center'}>
                          <button
                            onClick={() => removeItem(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            disabled={proformaForm.items.length === 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-gray-100 p-4 space-y-2">
                    <div className="flex justify-end gap-8 text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold w-24 text-right">£{formatCurrency(calculateSubtotal())}</span>
                    </div>
                    <div className="flex justify-end gap-8 text-sm">
                      <span className="text-gray-600">VAT (20%):</span>
                      <span className="font-semibold w-24 text-right">£{formatCurrency(calculateVAT())}</span>
                    </div>
                    <div className="flex justify-end gap-8 text-lg border-t pt-2">
                      <span className="font-bold">GRAND TOTAL:</span>
                      <span className="font-bold w-24 text-right" style={{ color: '#ee7203' }}>£{formatCurrency(calculateTotal())}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold">Additional Notes</Label>
                <Textarea
                  value={proformaForm.notes}
                  onChange={(e) => setProformaForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Data sheet available on request..."
                  rows={2}
                  className="mt-1"
                />
              </div>

              <Button
                data-testid="proforma-generate-download-btn"
                onClick={generateAndDownloadPDF}
                disabled={processing}
                className="w-full py-6 text-lg rounded-xl"
                style={{ backgroundColor: '#ee7203' }}
              >
                {processing ? 'Generating PDF...' : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    {proformaForm._editingProformaId ? 'Save Changes & Download PDF' : 'Generate & Download PDF'}
                  </>
                )}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Upload className="w-8 h-8" style={{ color: '#ee7203' }} />
                <h2 className="text-2xl font-bold" style={{ color: '#ee7203' }}>Upload Pro Forma PDF</h2>
              </div>

              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                  isDragging ? 'border-orange-500 bg-orange-50' : 'border-gray-300'
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                />
                
                {selectedFile ? (
                  <div>
                    <FileText className="w-16 h-16 mx-auto mb-4 text-green-600" />
                    <p className="font-medium text-lg mb-4">{selectedFile.name}</p>
                    <Button
                      onClick={() => handleFileUpload(selectedFile)}
                      disabled={processing}
                      size="lg"
                      style={{ backgroundColor: '#ee7203' }}
                    >
                      {processing ? 'Processing...' : 'Process & Download'}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600 text-lg mb-4">Drag & drop a PDF here, or</p>
                    <Button variant="outline" size="lg" onClick={() => fileInputRef.current?.click()}>
                      Browse Files
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main Proforma Component with Dropdown (legacy, can still be used standalone)
const Proforma = ({ user }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openModal = (mode) => {
    setModalMode(mode);
    setShowDropdown(false);
  };

  return (
    <>
      <div className="relative inline-block" ref={dropdownRef}>
        <Button
          onClick={() => setShowDropdown(!showDropdown)}
          className="gap-2"
          style={{ backgroundColor: '#ee7203' }}
        >
          <FileText className="w-4 h-4" />
          Pro Forma
          <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </Button>

        {showDropdown && (
          <div 
            className="absolute top-full left-0 mt-2 w-52 rounded-lg shadow-xl overflow-hidden z-50"
            style={{
              background: 'rgba(35, 35, 35, 0.98)',
              border: '2px solid rgba(238, 114, 3, 0.5)'
            }}
          >
            <button
              onClick={() => openModal('create')}
              className="w-full px-4 py-3 text-left text-white hover:bg-white/10 flex items-center gap-3 transition-colors"
            >
              <Plus className="w-4 h-4" style={{ color: '#ee7203' }} />
              Create New Pro Forma
            </button>
            <button
              onClick={() => openModal('recent')}
              className="w-full px-4 py-3 text-left text-white hover:bg-white/10 flex items-center gap-3 transition-colors border-t border-white/10"
            >
              <Clock className="w-4 h-4" style={{ color: '#ee7203' }} />
              Recent Pro Formas
            </button>
            <button
              onClick={() => openModal('upload')}
              className="w-full px-4 py-3 text-left text-white hover:bg-white/10 flex items-center gap-3 transition-colors border-t border-white/10"
            >
              <Upload className="w-4 h-4" style={{ color: '#ee7203' }} />
              Upload from PDF
            </button>
          </div>
        )}
      </div>

      <ProformaModal 
        isOpen={modalMode !== null} 
        onClose={() => setModalMode(null)} 
        mode={modalMode}
        user={user}
      />
    </>
  );
};

// Export the Modal component separately for use in M45NavPills
Proforma.Modal = ProformaModal;

export default Proforma;
